import React, { useEffect, useState } from "react";
import { Modal, Form, Row, Col, Table, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

interface RefundModalProps {
    show: boolean;
    onHide: () => void;
    refundData?: {
        customerId: number;
        customerName: string;
        customerMobileNo: string;
        refundableAmount: number;
    };
    onSubmitRefund: (data: any) => void;
}

interface RefundFormData {
    note?: string;
    type: string;
    reference: string;
    amount: number;
}

const RefundModal: React.FC<RefundModalProps> = ({
    show,
    onHide,
    refundData,
    onSubmitRefund,
}) => {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        trigger,
        formState: { errors },
    } = useForm<RefundFormData>();

    const [paymentList, setPaymentList] = useState<RefundFormData[]>([]);

    useEffect(() => {
        if (refundData) {
            reset({
                note: "",
                type: "",
                reference: "",
                amount: 0,
            });
            setPaymentList([]);
        }
    }, [refundData, reset]);

    const handleAddPayment = async () => {
        const isValid = await trigger(["type", "amount"]);

        if (!isValid) return;

        const type = watch("type");
        const reference = watch("reference");
        const amount = watch("amount");

        if (
            refundData &&
            paymentList.reduce((sum, p) => sum + Number(p.amount), 0) + Number(amount) >
            refundData.refundableAmount
        ) {
            toast.error("Total refund amount exceeds refundable balance!");
            return;
        }

        const newPayment: RefundFormData = { type, reference, amount };
        setPaymentList((prev) => [...prev, newPayment]);

        setValue("type", "");
        setValue("reference", "");
        setValue("amount", 0);
    };


    const handleRemovePayment = (index: number) => {
        setPaymentList((prev) => prev.filter((_, i) => i !== index));
    };

    const onSubmit = () => {
        if (paymentList.length === 0) {
            toast.warning("Please add at least one payment entry!");
            return;
        }

        const payload = {
            customerId: refundData?.customerId,
            note: watch("note"),
            payments: paymentList,
        };
        onSubmitRefund(payload);
    };


    return (
        <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Refund</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ height: "450px", minHeight: "400px", overflow: 'auto' }}>
                <h6 className="mb-3 fw-bold">Customer Details</h6>
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Customer Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={refundData?.customerName || ""}
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                type="text"
                                value={refundData?.customerMobileNo || ""}
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Refundable Balance</Form.Label>
                            <Form.Control
                                type="number"
                                value={refundData?.refundableAmount || 0}
                                disabled
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-4">
                    <Form.Label>Note</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter note..."
                        {...register("note")}
                    />
                </Form.Group>

                <h6 className="mb-3 fw-bold">Payment Method</h6>
                <Row className=" mb-3">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>
                                Type<span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select  {...register("type", { required: true })}>
                                <option value="">Select</option>
                                <option value="Cash">Cash</option>
                                <option value="UPI">UPI</option>
                            </Form.Select>
                            {errors.type && (
                                <small className="text-danger">Type is required</small>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Reference</Form.Label>
                            <Form.Control
                                type="tet"
                                placeholder="Enter reference"
                                {...register("reference")}
                                maxLength={150}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>
                                Amount<span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="Enter amount"
                                {...register("amount", {
                                    required: true,
                                    min: 0.01,
                                    max: refundData?.refundableAmount || 0,
                                    validate: (value) => {
                                        const regex = /^\d+(\.\d{1,2})?$/;
                                        return regex.test(value.toString()) || "Invalid amount format";
                                    },
                                    setValueAs: (v) => parseFloat(v),
                                })}
                                onKeyDown={(e) => {
                                    const allowedKeys = [
                                        "Backspace",
                                        "Delete",
                                        "ArrowLeft",
                                        "ArrowRight",
                                        "Tab",
                                        ".",
                                    ];

                                    if (
                                        (e.ctrlKey || e.metaKey) &&
                                        ["a", "c", "v", "x", "A", "C", "V", "X"].includes(e.key)
                                    ) {
                                        return;
                                    }

                                    if (
                                        (e.key >= "0" && e.key <= "9") ||
                                        allowedKeys.includes(e.key)
                                    ) {
                                        if (
                                            e.key === "." &&
                                            (e.currentTarget.value.includes(".") || e.currentTarget.value === "")
                                        ) {
                                            e.preventDefault();
                                        }
                                        return;
                                    }
                                    e.preventDefault();
                                }}
                            />

                            {errors.amount && (
                                <small className="text-danger">
                                    {errors.amount.message ||
                                        (errors.amount.type === "max"
                                            ? `Amount cannot exceed ${refundData?.refundableAmount}`
                                            : "Amount is required")}
                                </small>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={2}>
                        <Form.Group className="mt-4">
                            <Button className="submit-btn w-100 mt-1" onClick={handleAddPayment}>
                                Add
                            </Button>
                        </Form.Group>
                    </Col>

                </Row>

                {paymentList.length > 0 && (
                    <div className="mb-4 w-100">
                        <Table striped hover responsive>
                            <thead style={{ backgroundColor: "#b7002a" }}>
                                <tr>
                                    <th style={{ width: "100px", color: "white" }}>Actions</th>
                                    <th style={{ color: "white" }}>Type</th>
                                    <th style={{ color: "white" }}>Reference Number</th>
                                    <th style={{ color: "white" }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentList.map((payment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleRemovePayment(index)}
                                            >
                                                <i className="bx bx-trash" />
                                            </Button>
                                        </td>
                                        <td>{payment.type}</td>
                                        <td>{payment.reference || "-"}</td>
                                        <td>{payment.amount.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button className="btn add-btn" onClick={onHide}>
                    Cancel
                </button>
                <button className="btn submit-btn" onClick={onSubmit}>
                    Submit
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default RefundModal;
